/**
 * Counting visits.
 *
 * Nothing on this platform has ever counted a visitor, which was deliberate —
 * no cookies, no third-party script, nothing to disclose. Counting starts here,
 * and stays deliberate: what leaves the browser is a random per-tab id, the
 * league, and the section. No cookie, no IP, no account, no device, nothing
 * that survives the tab closing, and no way back from an id to a person.
 *
 * The arithmetic lives here rather than in the endpoint so it can be checked
 * without a database, and so the browser and the server agree on what a section
 * is called.
 */

/**
 * The sections a route can land on.
 *
 * An allowlist, and it matters that it is one. A default is what let two
 * leagues be scored by a third's rules: anything not named here is recorded as
 * 'other', never quietly filed under a real section where it would inflate that
 * section's count. A page added later reads as 'other' until it is added — a
 * visible gap rather than an invisible lie.
 */
export const SECTIONS = [
  'table',
  'fixtures',
  'teams',
  'match',
  'score',
  'post-match',
  'admin',
  'display',
  'platform',
  'other',
] as const;

export type Section = (typeof SECTIONS)[number];

export interface Place {
  /** The league's slug, or null for the platform's own pages. */
  league: string | null;
  section: Section;
}

const known = new Set<string>(SECTIONS);

/**
 * Where a hash route lands, reduced to a league and a section.
 *
 * Deliberately lossy. `#/l/premier-pearls/match/128` is recorded as Pearls'
 * match section and not as match 128: which fixture somebody opened is a
 * detail this has no business keeping, and the section is the whole of what a
 * sponsor is being told.
 */
export function placeOf(route: string): Place {
  const parts = route.split('/').filter(Boolean);
  if (parts[0] !== 'l' || !parts[1]) return { league: null, section: 'platform' };

  // A league's own landing page is its table; it has no section in the URL.
  const raw = parts[2] ?? 'table';
  return {
    league: parts[1],
    section: (known.has(raw) ? raw : 'other') as Section,
  };
}

/**
 * A new id for this tab's session.
 *
 * Random, not derived from anything about the visitor — deriving it from an IP
 * or a user agent would make it a fingerprint, which is the thing being avoided.
 */
export function newVisitId(): string {
  const bytes = new Uint8Array(12);
  crypto.getRandomValues(bytes);
  return btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** What the endpoint will accept as an id, so a junk value is dropped rather than stored. */
export const VISIT_ID = /^[A-Za-z0-9_-]{16}$/;

export interface DayCount {
  /** ISO date, Johannesburg's day. */
  day: string;
  visits: number;
  views: number;
}

/** Whole days from `from` to `to`, both ISO dates. */
export function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);
}

export interface Trend {
  current: number;
  previous: number;
  /** Proportional change, or null when there is no previous week to compare against. */
  change: number | null;
}

/**
 * The last seven days against the seven before them.
 *
 * Bucketed by the date itself rather than by position in the array, because a
 * day with no visits has no row: counting back seven entries would silently
 * reach into the week before and report the quiet week as busier than it was.
 *
 * `today` counts as day zero, so "the last seven days" is today and the six
 * behind it.
 */
export function weekOnWeek(days: DayCount[], today: string): Trend {
  let current = 0;
  let previous = 0;
  for (const day of days) {
    const age = daysBetween(day.day, today);
    if (age < 0 || age >= 14) continue;
    if (age < 7) current += day.visits;
    else previous += day.visits;
  }
  return {
    current,
    previous,
    change: previous === 0 ? null : (current - previous) / previous,
  };
}
